/* ──────────────────────────────────────────────────────────────────────
 *  FlipTeamMemberCard — 3D-flip wrapper around TeamMemberCard
 *
 *  Front face: Standard TeamMemberCard (avatar, name, role).
 *  Back face:  Depends on the member type:
 *
 *    L4E member (userGuid set):
 *      Impact signature mini-radar + View Profile button.
 *      Message button ONLY shown when `isContact` is true (i.e., the
 *      current viewer has an accepted contact relationship with this user).
 *      Non-contacts must visit the profile page to initiate messaging,
 *      where the target user's privacy settings govern access.
 *
 *    External person (no userGuid, but has email/profileUrl):
 *      Email button and/or Visit Profile (external link) button.
 *      Shows whichever contacts are available.
 *
 *    No contact info at all → card does NOT flip.
 *
 *  Interaction model:
 *    Desktop (pointer: fine) — mouseenter flips, mouseleave flips back
 *    Mobile  (pointer: coarse / none) — tap toggles flip state
 *
 *  Skips flip entirely for vacant / redacted cards and cards with no
 *  actionable back-face content.
 * ────────────────────────────────────────────────────────────────────── */

import React, { useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { useApiImpactSignature } from '../../api/useApiImpactSignature';
import TeamMemberCard, { type TeamMemberCardProps } from './TeamMemberCard';
import ImpactSignatureMini from '../DimensionRadar/ImpactSignatureMini';
import styles from './FlipTeamMemberCard.module.css';

/* ── Props ────────────────────────────────────────────────────────── */

export interface FlipTeamMemberCardProps extends TeamMemberCardProps {
  /** The L4E user GUID — when set, back face shows impact radar + platform actions. */
  userGuid?: string | null;
  /** Display name shown on the back face header. */
  displayName: string;
  /**
   * Whether the current viewer has an accepted contact relationship with this user.
   * When true, the back face shows a "Message" button alongside "View Profile".
   * When false/undefined, only "View Profile" is shown — the user must visit the
   * profile page to initiate contact, where privacy settings govern access.
   *
   * Parent components are responsible for resolving this from the contacts API.
   * See DEV-538 for the wiring task.
   */
  isContact?: boolean;
  /**
   * Contact email for non-L4E members.
   * When set (and no userGuid), the back face shows an Email button.
   */
  contactEmail?: string | null;
  /**
   * External profile URL for non-L4E members (LinkedIn, personal site, etc.).
   * When set (and no userGuid), the back face shows a Visit Profile button.
   * Note: this is separate from TeamMemberCard's `externalProfileUrl` prop,
   * which controls the front-face name link. Both can be the same URL.
   */
  contactProfileUrl?: string | null;
  /**
   * Optional platform-role descriptor surfaced ONLY on the back face,
   * under the name header. Used by the Geo Community Leaders surface
   * (POLICY_TEAM_ROSTERS.md §4.3) to display a member's V2 community
   * permission bundle ("Community Owner", "Community Admin", etc.)
   * without crowding the public-facing front of the card.
   *
   * Deliberately back-face-only: platform metadata is information for
   * curious viewers and admins, not signage attached to a person's
   * portrait. Surfaces here without competing for visual space against
   * the person's photo, name, and role title.
   */
  backFaceBadge?: string | null;
  /**
   * DEV-1164 follow-up (2026-06-01). Optional badge(s) rendered as an
   * absolute-positioned overlay INSIDE the front face. Unlike the
   * legacy pattern of rendering Lead/You chips as siblings to the
   * card, this prop ensures the badges rotate with the card on flip
   * (going behind on hover). Used by the Engagements team grid where
   * Skip flagged the static Lead chip as visually wrong.
   *
   * Accepts ReactNode rather than a string so callers can compose
   * multiple chips (Lead + You + future markers).
   */
  frontFaceBadge?: React.ReactNode;
}

/* ── Helpers ──────────────────────────────────────────────────────── */

/**
 * Detect coarse pointer (touch device).
 * Falls back to false (desktop) when matchMedia is unavailable.
 */
function isTouchDevice(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(pointer: coarse)').matches;
}

/* ── Inline SVG icons ─────────────────────────────────────────────── */

const IconChat = () => (
  <svg
    width="14" height="14" viewBox="0 0 24 24"
    fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
  >
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

const IconPerson = () => (
  <svg
    width="14" height="14" viewBox="0 0 24 24"
    fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
  >
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const IconMail = () => (
  <svg
    width="14" height="14" viewBox="0 0 24 24"
    fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
  >
    <rect x="2" y="4" width="20" height="16" rx="2" />
    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
  </svg>
);

const IconExternalLink = () => (
  <svg
    width="14" height="14" viewBox="0 0 24 24"
    fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
  >
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    <polyline points="15 3 21 3 21 9" />
    <line x1="10" y1="14" x2="21" y2="3" />
  </svg>
);

/* ── Component ────────────────────────────────────────────────────── */

export default function FlipTeamMemberCard({
  userGuid,
  displayName,
  isContact = false,
  contactEmail,
  contactProfileUrl,
  backFaceBadge,
  frontFaceBadge,
  ...cardProps
}: FlipTeamMemberCardProps) {
  const { t } = useAppTranslation('common');
  const navigate = useNavigate();

  /* ── Flip state ── */
  const [flipped, setFlipped] = useState(false);
  const isTouch = useRef(isTouchDevice());

  /* ── Determine flip eligibility ── */
  const isL4E = !!userGuid;
  const hasExternalContact = !isL4E && (!!contactEmail || !!contactProfileUrl);
  const canFlip =
    !cardProps.isVacant &&
    !cardProps.isRedacted &&
    (isL4E || hasExternalContact);

  /* ── Impact data (only fetched for L4E members) ── */
  const { tiers } = useApiImpactSignature(isL4E && canFlip ? userGuid : null);

  /* ── Interaction handlers ── */
  const handleMouseEnter = useCallback(() => {
    if (!canFlip || isTouch.current) return;
    setFlipped(true);
  }, [canFlip]);

  const handleMouseLeave = useCallback(() => {
    if (!canFlip || isTouch.current) return;
    setFlipped(false);
  }, [canFlip]);

  const handleTap = useCallback(() => {
    if (!canFlip || !isTouch.current) return;
    setFlipped((prev) => !prev);
  }, [canFlip]);

  /* ── L4E action handlers ── */
  const handleMessage = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!userGuid) return;
      navigate(`/chat?with=${userGuid}`);
    },
    [navigate, userGuid],
  );

  const handleViewProfile = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!userGuid) return;
      navigate(`/profile/${userGuid}`);
    },
    [navigate, userGuid],
  );

  /* ── External action handlers ── */
  const handleEmail = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!contactEmail) return;
      window.open(`mailto:${contactEmail}`, '_blank');
    },
    [contactEmail],
  );

  const handleExternalProfile = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!contactProfileUrl) return;
      window.open(contactProfileUrl, '_blank', 'noopener,noreferrer');
    },
    [contactProfileUrl],
  );

  /* ── If the card can't flip, render the plain TeamMemberCard ── */
  if (!canFlip) {
    return (
      <TeamMemberCard
        displayName={displayName}
        {...cardProps}
      />
    );
  }

  /* ── First name for the back-face header ── */
  const firstName = displayName.trim().split(/\s+/)[0] || displayName;

  /* ── Size-aware class (match container to the card size variant) ── */
  const isLg = cardProps.size === 'lg';
  const containerCls = `${styles.flipContainer} ${isLg ? styles.flipContainerLg : ''}`;
  const innerCls = `${styles.flipInner} ${isLg ? styles.flipInnerLg : ''} ${flipped ? styles.flipped : ''}`;

  /* ── Render ── */
  return (
    <div
      className={containerCls}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleTap}
      role={isTouch.current ? 'button' : undefined}
      tabIndex={isTouch.current ? 0 : undefined}
      aria-label={isTouch.current ? t('teamMemberCard.tapToFlip') : undefined}
    >
      <div className={innerCls}>
        {/* ── FRONT FACE ── */}
        <div className={styles.flipFront}>
          <TeamMemberCard displayName={displayName} {...cardProps} />
          {/* DEV-1164 follow-up (2026-06-01): badges attached to the
              front face so they rotate with the card on flip. Engagements
              team grid uses this for Lead / You chips that previously
              sat as siblings to FlipTeamMemberCard and didn't flip. */}
          {frontFaceBadge && (
            <div className={styles.frontFaceBadgeSlot}>
              {frontFaceBadge}
            </div>
          )}
        </div>

        {/* ── BACK FACE ── */}
        <div className={styles.flipBack}>
          <div className={styles.backContent}>
            {/* Name header */}
            <span className={styles.backName}>{firstName}</span>

            {/* Platform-role descriptor (POLICY_TEAM_ROSTERS.md §4.3).
                Rendered here on the back face — deliberately not on the
                front, to keep platform metadata out of the public
                portrait. Inline style keeps the change non-invasive on
                the existing styles module. */}
            {backFaceBadge && (
              <span
                style={{
                  display: "inline-block",
                  marginBlockStart: 2,
                  padding: "2px 10px",
                  fontSize: 11,
                  fontWeight: 600,
                  color: "var(--text-primary)",
                  background: "var(--accent-secondary)",
                  borderRadius: 10,
                }}
              >
                {backFaceBadge}
              </span>
            )}

            {/* ── L4E back face: impact radar + platform actions ── */}
            {isL4E && (
              <>
                <div className={styles.radarWrap}>
                  {tiers ? (
                    <ImpactSignatureMini tiers={tiers} />
                  ) : (
                    <span className={styles.noImpact}>
                      {t('teamMemberCard.noImpactYet')}
                    </span>
                  )}
                </div>

                <div className={styles.backActions}>
                  {isContact && (
                    <button
                      type="button"
                      className={styles.actionBtn}
                      onClick={handleMessage}
                      title={t('teamMemberCard.sendMessage')}
                    >
                      <IconChat />
                      <span>{t('teamMemberCard.sendMessage')}</span>
                    </button>
                  )}
                  <button
                    type="button"
                    className={styles.actionBtn}
                    onClick={handleViewProfile}
                    title={t('teamMemberCard.viewProfile')}
                  >
                    <IconPerson />
                    <span>{t('teamMemberCard.viewProfile')}</span>
                  </button>
                </div>
              </>
            )}

            {/* ── External back face: email + profile link ── */}
            {!isL4E && hasExternalContact && (
              <div className={styles.externalActions}>
                {contactEmail && (
                  <button
                    type="button"
                    className={styles.actionBtn}
                    onClick={handleEmail}
                    title={contactEmail}
                  >
                    <IconMail />
                    <span>{t('teamMemberCard.email')}</span>
                  </button>
                )}
                {contactProfileUrl && (
                  <button
                    type="button"
                    className={styles.actionBtn}
                    onClick={handleExternalProfile}
                    title={t('teamMemberCard.visitProfile')}
                  >
                    <IconExternalLink />
                    <span>{t('teamMemberCard.visitProfile')}</span>
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
